import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import {
  getKnowledgeById,
  searchKnowledgePdfs,
  searchKnowledgeMarkdown,
  searchKnowledgeLinks,
  removePdf,
  removeMarkdown,
  removeLink,
  uploadPdf,
  uploadMarkdown,
  replaceMarkdownFile,
  DuplicateMarkdownError,
  addLink,
  classifyFile,
  updateKnowledge,
} from '@/api/knowledge';
import {
  getAgentsForKnowledge,
  linkAgentToKnowledge,
  unlinkAgentFromKnowledge,
} from '@/api/agentKnowledge';
import type {
  Knowledge,
  KnowledgePdf,
  KnowledgeMarkdown,
  KnowledgeLink,
  AiAgentKnowledge,
  UnifiedSource,
} from '@/api/types';
import type { RootStore } from './RootStore';

export interface UploadTask {
  id: string;       // generated client-side
  file: File;
  kind: 'pdf' | 'markdown' | 'unsupported';
  // 'duplicate' is a recoverable error state — the file with the same Title
  // already exists in this KB. UI offers Skip / Replace existing actions
  // rather than a blind Retry that would fail the same way.
  status: 'queued' | 'uploading' | 'success' | 'error' | 'duplicate';
  percent: number;
  error?: string;
}

// Cap parallel uploads. The previous code fired all N requests at once which
// burst-saturated SQL + Blob Storage + Azure AI Search for large drops (e.g.
// the 17-file Diabetes KB seed). Three concurrent uploads is the empirical
// sweet spot — enough to keep network bandwidth busy, low enough to keep
// any single Azure-side throttle from cascading into orphan rows.
const MAX_CONCURRENT_UPLOADS = 3;

// Debounce for the post-upload reconciliation refresh. Each successful upload
// is added to the local list optimistically (the API now returns the saved
// entity), but we still do ONE reconciling refresh after the whole queue
// drains to catch anything the backend may have computed (DocumentId,
// CitationDisplayName, etc.) that we don't have client-side yet.
const RECONCILE_DEBOUNCE_MS = 800;

// One KB's full state: the KB record + all three source types + in-flight uploads.
// Sources are merged into a unified list for the UI to render in one table.

export class KnowledgeDetailStore {
  @observable knowledgeId: number | null = null;
  @observable knowledge: Knowledge | null = null;
  @observable pdfs: KnowledgePdf[] = [];
  @observable markdowns: KnowledgeMarkdown[] = [];
  @observable links: KnowledgeLink[] = [];

  @observable isLoading = false;
  @observable error: string | null = null;

  // Agents linked to this KB. Loaded by loadFor in parallel with the source
  // fetches; mutated by linkAgent / unlinkAgent. Each entry's `agent` nav is
  // populated by AiAgentKnowledgeRepository's projection so cards render
  // without per-row round-trips.
  @observable linkedAgents: AiAgentKnowledge[] = [];
  @observable isLinkingAgents = false;

  // Upload state. `uploads` is the visible list (observed by the UI).
  // `uploadQueue` and `activeUploadCount` enforce the concurrency cap.
  @observable uploads: UploadTask[] = [];
  @observable private uploadQueue: string[] = [];
  @observable private activeUploadCount = 0;
  private reconcileTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
  }

  /** Aggregate counts for the upload-summary chip at the top of the panel. */
  @computed get uploadSummary(): {
    total: number;
    queued: number;
    uploading: number;
    success: number;
    error: number;
    duplicate: number;
    /** True once nothing is in flight or queued — UI uses this to fade the panel. */
    allDone: boolean;
  } {
    let queued = 0, uploading = 0, success = 0, error = 0, duplicate = 0;
    for (const u of this.uploads) {
      if (u.status === 'queued') queued++;
      else if (u.status === 'uploading') uploading++;
      else if (u.status === 'success') success++;
      else if (u.status === 'error') error++;
      else if (u.status === 'duplicate') duplicate++;
    }
    return {
      total: this.uploads.length,
      queued,
      uploading,
      success,
      error,
      duplicate,
      allDone: queued === 0 && uploading === 0,
    };
  }

  @computed get sources(): UnifiedSource[] {
    const out: UnifiedSource[] = [];
    for (const p of this.pdfs) {
      out.push({
        kind: 'pdf',
        id: p.id,
        knowledgeId: p.knowledgeId,
        title: p.title || '(untitled PDF)',
        description: p.description,
        blobUrl: p.blobWithSharedAccessKey,
        documentId: p.documentId,
        tags: p.tags,
        createdDate: p.createdDate,
      });
    }
    for (const m of this.markdowns) {
      out.push({
        kind: 'markdown',
        id: m.id,
        knowledgeId: m.knowledgeId,
        title: m.title || '(untitled Markdown)',
        description: m.description,
        blobUrl: m.blobWithSharedAccessKey,
        documentId: m.documentId,
        tags: m.tags,
        createdDate: m.createdDate,
      });
    }
    for (const l of this.links) {
      out.push({
        kind: 'link',
        id: l.id,
        knowledgeId: l.knowledgeId,
        title: l.title || l.onlineLink || '(untitled link)',
        description: l.description,
        url: l.onlineLink,
        documentId: l.documentId,
        tags: l.tags,
        createdDate: l.createdDate,
      });
    }
    // Newest first (best for "what did I just upload?")
    return out.sort((a, b) => (b.createdDate ?? '').localeCompare(a.createdDate ?? ''));
  }

  @computed get sourceCounts(): { pdf: number; markdown: number; link: number; total: number } {
    return {
      pdf: this.pdfs.length,
      markdown: this.markdowns.length,
      link: this.links.length,
      total: this.pdfs.length + this.markdowns.length + this.links.length,
    };
  }

  // Azure AI Search indexing health derived from `documentId` presence on each
  // source. A non-null documentId means the file was successfully chunked,
  // embedded, and is queryable by RAG. Surfacing this saves an "ask the agent
  // and watch it answer wrong" debug cycle.
  @computed get indexedCounts(): { indexed: number; pending: number; total: number } {
    const allSources = [...this.pdfs, ...this.markdowns, ...this.links];
    const indexed = allSources.filter((s) => !!s.documentId).length;
    return {
      indexed,
      pending: allSources.length - indexed,
      total: allSources.length,
    };
  }

  @action
  async loadFor(id: number): Promise<void> {
    if (this.knowledgeId !== id) {
      this.knowledge = null;
      this.pdfs = [];
      this.markdowns = [];
      this.links = [];
      this.linkedAgents = [];
      this.uploads = [];
    }
    this.knowledgeId = id;
    this.isLoading = true;
    this.error = null;

    // Fetch in parallel; partial failures populate what we can.
    const [kRes, pdfRes, mdRes, linkRes, agentLinkRes] = await Promise.allSettled([
      getKnowledgeById(id),
      searchKnowledgePdfs(id),
      searchKnowledgeMarkdown(id),
      searchKnowledgeLinks(id),
      getAgentsForKnowledge(id),
    ]);

    runInAction(() => {
      if (kRes.status === 'fulfilled') this.knowledge = kRes.value;
      if (pdfRes.status === 'fulfilled') this.pdfs = pdfRes.value;
      if (mdRes.status === 'fulfilled') this.markdowns = mdRes.value;
      if (linkRes.status === 'fulfilled') this.links = linkRes.value;
      if (agentLinkRes.status === 'fulfilled') this.linkedAgents = agentLinkRes.value;

      const failed = [kRes, pdfRes, mdRes, linkRes, agentLinkRes].filter((r) => r.status === 'rejected');
      if (kRes.status === 'rejected') {
        this.error = kRes.reason instanceof Error ? kRes.reason.message : 'Failed to load knowledge base';
      } else if (failed.length > 0) {
        // Some content type failed; surface a non-fatal warning.
        this.error =
          'Some sources failed to load: ' +
          failed
            .map((r) => (r.status === 'rejected' ? (r.reason instanceof Error ? r.reason.message : 'unknown') : ''))
            .filter(Boolean)
            .join('; ');
      }
      this.isLoading = false;
    });
  }

  @action
  refresh(): void {
    if (this.knowledgeId != null) void this.loadFor(this.knowledgeId);
  }

  @action
  clear(): void {
    this.knowledgeId = null;
    this.knowledge = null;
    this.pdfs = [];
    this.markdowns = [];
    this.links = [];
    this.linkedAgents = [];
    this.uploads = [];
    this.error = null;
  }

  // ── Linked-agent mutations ──────────────────────────────────────────────
  // Both write the same ai.AiAgentKnowledge rows the agent's Knowledge tab
  // does. Backend validates merchant boundary + uniqueness; we surface the
  // backend's friendly message verbatim on conflict.

  @action
  async linkAgent(agentId: number): Promise<void> {
    if (this.knowledgeId == null) throw new Error('No knowledge base loaded.');
    this.isLinkingAgents = true;
    try {
      await linkAgentToKnowledge(this.knowledgeId, agentId);
      // Re-fetch the linked-agents list; cheaper than a full refresh, and the
      // backend's projection populates the agent nav we need to render.
      const fresh = await getAgentsForKnowledge(this.knowledgeId);
      runInAction(() => { this.linkedAgents = fresh; });
    } finally {
      runInAction(() => { this.isLinkingAgents = false; });
    }
  }

  @action
  async unlinkAgent(linkId: number): Promise<void> {
    await unlinkAgentFromKnowledge(linkId);
    runInAction(() => {
      this.linkedAgents = this.linkedAgents.filter((l) => l.id !== linkId);
    });
  }

  // ── Mutations ────────────────────────────────────────────────────────────

  @action
  async deleteSource(kind: 'pdf' | 'markdown' | 'link', id: number): Promise<void> {
    if (kind === 'pdf') await removePdf(id);
    else if (kind === 'markdown') await removeMarkdown(id);
    else await removeLink(id);
    runInAction(() => {
      if (kind === 'pdf') this.pdfs = this.pdfs.filter((p) => p.id !== id);
      else if (kind === 'markdown') this.markdowns = this.markdowns.filter((m) => m.id !== id);
      else this.links = this.links.filter((l) => l.id !== id);
    });
  }

  @action
  async deleteMany(items: { kind: 'pdf' | 'markdown' | 'link'; id: number }[]): Promise<void> {
    // Best-effort parallel deletes; failures stay in the list (we'll re-fetch).
    await Promise.allSettled(items.map((it) => this.deleteSource(it.kind, it.id)));
    void this.refresh();
  }

  // ── Uploads ──────────────────────────────────────────────────────────────
  //
  // Multi-file upload designed for non-tech-savvy users (e.g. a clinical SME
  // dropping a 17-file Markdown KB at once):
  //
  //   - Concurrency capped at MAX_CONCURRENT_UPLOADS so a big drop doesn't
  //     burst-saturate Azure SQL / Blob / Search and produce orphan rows.
  //   - Per-file progress + plain-English error text on every row.
  //   - Successful upload optimistically adds the saved entity to the local
  //     list (no waiting for a refresh round-trip).
  //   - One reconciliation refresh after the whole queue drains (debounced),
  //     not one per file.
  //   - Duplicate detection ('A Markdown source titled X already exists') is
  //     surfaced as a distinct status with two clear actions: Skip or Replace.

  @action
  enqueueUploads(files: File[]): UploadTask[] {
    const tasks: UploadTask[] = files.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      kind: classifyFile(f),
      status: 'queued',
      percent: 0,
    }));
    this.uploads = [...this.uploads, ...tasks];
    for (const t of tasks) {
      // Unsupported files fail immediately so they don't take a queue slot.
      if (t.kind === 'unsupported') {
        t.status = 'error';
        t.error = 'Unsupported file type. Please use PDF, Markdown (.md / .markdown), or text (.txt).';
        continue;
      }
      this.uploadQueue.push(t.id);
    }
    this.processQueue();
    return tasks;
  }

  /**
   * Pulls task IDs off the queue and starts uploads, respecting the
   * concurrency cap. Re-runs whenever an upload finishes or new tasks
   * are enqueued.
   */
  @action
  private processQueue(): void {
    while (this.activeUploadCount < MAX_CONCURRENT_UPLOADS && this.uploadQueue.length > 0) {
      const taskId = this.uploadQueue.shift()!;
      this.activeUploadCount++;
      void this.runUpload(taskId).finally(() => {
        runInAction(() => {
          this.activeUploadCount--;
        });
        this.processQueue();
        this.scheduleReconcile();
      });
    }
  }

  /**
   * Single full-list refresh after the upload queue drains. We've already
   * optimistically added each saved entity, so this is reconciliation only —
   * picks up any server-computed fields (DocumentId, CitationDisplayName)
   * that we don't have client-side. Debounced so a fast queue doesn't
   * trigger N refreshes.
   */
  private scheduleReconcile(): void {
    if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
    this.reconcileTimer = setTimeout(() => {
      this.reconcileTimer = null;
      // Only fire when the queue has truly drained.
      if (this.uploadQueue.length === 0 && this.activeUploadCount === 0) {
        void this.refreshPdfs();
        void this.refreshMarkdowns();
      }
    }, RECONCILE_DEBOUNCE_MS);
  }

  @action
  private async runUpload(taskId: string): Promise<void> {
    const task = this.uploads.find((t) => t.id === taskId);
    if (!task || this.knowledgeId == null) return;

    runInAction(() => {
      task.status = 'uploading';
      task.percent = 0;
      task.error = undefined;
    });

    try {
      const onProgress = (p: number) => {
        runInAction(() => {
          const t = this.uploads.find((x) => x.id === taskId);
          if (t) t.percent = p;
        });
      };
      if (task.kind === 'pdf') {
        // PDF upload still returns void; refresh handles the local list update.
        await uploadPdf(this.knowledgeId, task.file, {}, onProgress);
      } else {
        // Markdown upload returns the saved entity — push it directly to the
        // local list so the UI updates instantly without waiting for refresh.
        const saved = await uploadMarkdown(this.knowledgeId, task.file, {}, onProgress);
        runInAction(() => {
          // Guard against double-add if reconcile races: only insert if not present.
          if (!this.markdowns.some((m) => m.id === saved.id)) {
            this.markdowns = [saved, ...this.markdowns];
          }
        });
      }
      runInAction(() => {
        task.status = 'success';
        task.percent = 100;
      });
      // Auto-clear the row after a beat so the user sees the green tick but
      // the panel doesn't grow indefinitely on big uploads.
      setTimeout(() => {
        runInAction(() => {
          this.uploads = this.uploads.filter((t) => t.id !== taskId);
        });
      }, 1500);
    } catch (err) {
      runInAction(() => {
        if (err instanceof DuplicateMarkdownError) {
          task.status = 'duplicate';
          task.error = err.message;
        } else {
          task.status = 'error';
          task.error = err instanceof Error ? err.message : 'Upload failed. Please try again.';
        }
      });
    }
  }

  @action
  retryUpload(taskId: string): void {
    const task = this.uploads.find((t) => t.id === taskId);
    if (!task) return;
    // Re-queue rather than running directly so the concurrency cap still applies.
    runInAction(() => {
      task.status = 'queued';
      task.error = undefined;
      task.percent = 0;
    });
    this.uploadQueue.push(taskId);
    this.processQueue();
  }

  /**
   * Replace-existing flow for duplicate-titled uploads. Looks up the existing
   * Markdown source by Title and swaps its file content via the ReplaceFile
   * endpoint. Metadata (description, tags) on the existing record is preserved.
   * Used by the "Replace existing" button on a 'duplicate'-status upload row.
   */
  @action
  async replaceExistingMarkdown(taskId: string): Promise<void> {
    const task = this.uploads.find((t) => t.id === taskId);
    if (!task || this.knowledgeId == null || task.kind !== 'markdown') return;

    const targetTitle = task.file.name.replace(/\.(md|markdown)$/i, '');
    const existing = this.markdowns.find((m) => m.title === targetTitle);
    if (!existing) {
      runInAction(() => {
        task.status = 'error';
        task.error =
          `Couldn't find the existing source titled "${targetTitle}" to replace. ` +
          `It may have been deleted. Try refreshing and uploading again.`;
      });
      return;
    }

    runInAction(() => {
      task.status = 'uploading';
      task.percent = 0;
      task.error = undefined;
    });

    try {
      await replaceMarkdownFile(existing.id, task.file, (p) => {
        runInAction(() => {
          const t = this.uploads.find((x) => x.id === taskId);
          if (t) t.percent = p;
        });
      });
      runInAction(() => {
        task.status = 'success';
        task.percent = 100;
      });
      setTimeout(() => {
        runInAction(() => {
          this.uploads = this.uploads.filter((t) => t.id !== taskId);
        });
      }, 1500);
      // Refresh the Markdown list so the swapped CitationDisplayName / DocumentId
      // are visible in the sources table without manual refresh.
      void this.refreshMarkdowns();
    } catch (err) {
      runInAction(() => {
        task.status = 'error';
        task.error = err instanceof Error ? err.message : 'Replace failed. Please try again.';
      });
    }
  }

  /** Skip (dismiss) a single upload row — e.g. the user chose not to replace a duplicate. */
  @action
  cancelUpload(taskId: string): void {
    this.uploads = this.uploads.filter((t) => t.id !== taskId);
    this.uploadQueue = this.uploadQueue.filter((id) => id !== taskId);
  }

  /** Clear all completed (success / skipped) and failed rows in one click. */
  @action
  clearFinishedUploads(): void {
    this.uploads = this.uploads.filter(
      (t) => t.status === 'queued' || t.status === 'uploading'
    );
  }

  @action
  async addLinkSource(url: string, title?: string, description?: string): Promise<void> {
    if (this.knowledgeId == null) return;
    await addLink({
      knowledgeId: this.knowledgeId,
      onlineLink: url,
      title,
      description,
    });
    await this.refreshLinks();
  }

  @action
  async refreshPdfs(): Promise<void> {
    if (this.knowledgeId == null) return;
    const items = await searchKnowledgePdfs(this.knowledgeId);
    runInAction(() => { this.pdfs = items; });
  }

  @action
  async refreshMarkdowns(): Promise<void> {
    if (this.knowledgeId == null) return;
    const items = await searchKnowledgeMarkdown(this.knowledgeId);
    runInAction(() => { this.markdowns = items; });
  }

  @action
  async refreshLinks(): Promise<void> {
    if (this.knowledgeId == null) return;
    const items = await searchKnowledgeLinks(this.knowledgeId);
    runInAction(() => { this.links = items; });
  }

  @action
  async updateMetadata(input: { title?: string; description?: string }): Promise<void> {
    if (this.knowledgeId == null || !this.knowledge) return;
    const updated = await updateKnowledge(this.knowledgeId, {
      ...this.knowledge,
      ...input,
    });
    runInAction(() => { this.knowledge = updated; });
  }
}
